import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from './components/Layout'
import { DashboardPage } from './pages/Dashboard'
import { MeetingCenterPage } from './pages/MeetingCenter'
import { WeeklyStatusPage } from './pages/WeeklyStatus'
import { EvidenceHistoryPage } from './pages/EvidenceHistory'
import { GiftOperationsPage } from './pages/GiftOperations'
import { BoxPage } from './pages/Box'
import { AdaptersPage } from './pages/Adapters'
import { AclPage } from './pages/Acl'
import { AccessManagementPage } from './pages/AccessManagement'
import { AdminPage } from './pages/Admin'
import { ProposalsPage } from './pages/Proposals'
import { ProposalDetailPage } from './pages/ProposalDetail'
import { KpiDashboardPage } from './pages/KpiDashboard'
import { FunnelDashboardPage } from './pages/FunnelDashboard'
import { TeamPulsePage } from './pages/TeamPulse'
import { MasteryMapPage } from './pages/MasteryMap'
import { MemoryPage } from './pages/Memory'
import { MonitoringPage } from './pages/Monitoring'
import { OnboardingPage } from './pages/Onboarding'
import { LoginPage } from './pages/Login'
import { SignupPage } from './pages/Signup'
import { WorkspacesPage } from './pages/Workspaces'
import { StatsPage } from './pages/Stats'
import { BenchmarkCurationPage } from './pages/BenchmarkCuration'
import { JLocomoCurationPage } from './pages/JLocomoCuration'
import { SummaryOverviewPage } from './pages/summary/SummaryOverviewPage'
import { SummaryCallsPage } from './pages/summary/SummaryCallsPage'
import { SummaryAiSdrPage } from './pages/summary/SummaryAiSdrPage'
import { SummaryAxPage } from './pages/summary/SummaryAxPage'
import { useAppAuth } from './lib/auth'
import {
  defaultHref,
  hasAnyNavScreen,
  isScreenEnabled,
  resolveDefaultLandingHref,
  type ScreenId,
} from './lib/nav-config'
import { defaultScreen } from './lib/runtime-config'
import { useScreenSet } from './lib/use-screen-set'

function LoadingSpinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAppAuth()

  if (!isLoaded) {
    return <LoadingSpinner />
  }

  if (!isSignedIn) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  // WU-04: runtime-config の ui.enabledScreens で route を filter。
  // WU-07: capability bootstrap（/admin/me/bootstrap）との積集合。
  // 取得完了まで spinner（全画面露出フラッシュ・誤 redirect 防止）。
  // 無効画面への直接 URL アクセスは catch-all で有効画面へ redirect する。
  const screenSet = useScreenSet()

  if (screenSet.status === 'loading') {
    return <LoadingSpinner />
  }

  const enabled = screenSet.screens
  const on = (screen: ScreenId) => isScreenEnabled(screen, enabled)

  // WU-08b: ui.defaultScreen（CH_UI_DEFAULT_SCREEN）。未設定・無効値・無効化された
  // 画面は null（現行挙動維持）。dashboard の href は永続 alias "/dashboard" のため
  // landingHref が '/' と一致することは基本無いが、将来 screen 追加時の防御として
  // ガードは残す（一致時は Navigate せず現行 DashboardPage を "/" に直描画）。
  const landingHref = resolveDefaultLandingHref(defaultScreen(), enabled)

  // capability × deploy の積集合が空（nav に出せる画面ゼロ）のときは route を張らない。
  // catch-all の Navigate が defaultHref('/') と往復して無限 redirect になるのを防ぐ。
  if (!hasAnyNavScreen(enabled)) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-2 bg-background text-center">
        <p className="text-lg font-semibold">表示可能な画面がありません</p>
        <p className="text-sm text-muted-foreground">
          アクセス権限とデプロイ設定をご確認のうえ、管理者に連絡してください。
        </p>
      </div>
    )
  }

  return (
    <Layout screens={enabled}>
      <Routes>
        {/* review fix: dashboard は landing 設定に関わらず常に "/dashboard" で到達
            できる（"/" が他 screen の landing に奪われても死にリンクにしない）。*/}
        {on('dashboard') && <Route path="/dashboard" element={<DashboardPage />} />}
        {landingHref && landingHref !== '/' ? (
          <Route path="/" element={<Navigate to={landingHref} replace />} />
        ) : (
          on('dashboard') && <Route path="/" element={<DashboardPage />} />
        )}
        {on('weekly-status') && (
          <Route path="/weekly-status" element={<WeeklyStatusPage />} />
        )}
        {on('meeting-center') && (
          <Route path="/meeting-center" element={<MeetingCenterPage />} />
        )}
        {on('evidence-history') && (
          <Route path="/evidence-history" element={<EvidenceHistoryPage />} />
        )}
        {on('gift-operations') && (
          <Route path="/gift-operations" element={<GiftOperationsPage />} />
        )}
        {on('box') && <Route path="/box" element={<BoxPage />} />}
        {on('adapters') && <Route path="/adapters" element={<AdaptersPage />} />}
        {on('acl') && <Route path="/acl" element={<AclPage />} />}
        {on('access-management') && (
          <Route path="/access-management" element={<AccessManagementPage />} />
        )}
        {on('proposals') && <Route path="/proposals" element={<ProposalsPage />} />}
        {on('proposals') && (
          <Route path="/proposals/:proposalId" element={<ProposalDetailPage />} />
        )}
        {on('kpi') && <Route path="/kpi" element={<KpiDashboardPage />} />}
        {on('funnel') && <Route path="/funnel" element={<FunnelDashboardPage />} />}
        {on('team-pulse') && <Route path="/team-pulse" element={<TeamPulsePage />} />}
        {on('mastery') && <Route path="/mastery" element={<MasteryMapPage />} />}
        {on('monitoring') && <Route path="/monitoring" element={<MonitoringPage />} />}
        {on('onboarding') && <Route path="/onboarding" element={<OnboardingPage />} />}
        {on('memory-explore') && (
          <Route path="/memory-explore" element={<MemoryPage />} />
        )}
        {on('memory-explore') && (
          <Route path="/memory" element={<Navigate to="/memory-explore" replace />} />
        )}
        {on('admin') && <Route path="/admin" element={<AdminPage />} />}
        {on('benchmark-curation') && (
          <Route path="/benchmark-curation" element={<BenchmarkCurationPage />} />
        )}
        {on('j-locomo-curation') && (
          <Route path="/j-locomo-curation" element={<JLocomoCurationPage />} />
        )}
        {on('workspaces') && <Route path="/workspaces" element={<WorkspacesPage />} />}
        {on('stats') && <Route path="/stats" element={<StatsPage />} />}
        {on('summary-overview') && (
          <Route path="/summary" element={<SummaryOverviewPage />} />
        )}
        {on('summary-calls') && (
          <Route path="/summary/calls" element={<SummaryCallsPage />} />
        )}
        {on('summary-ai-sdr') && (
          <Route path="/summary/ai-sdr" element={<SummaryAiSdrPage />} />
        )}
        {on('summary-ax') && <Route path="/summary/ax" element={<SummaryAxPage />} />}
        <Route path="*" element={<Navigate to={defaultHref(enabled)} replace />} />
      </Routes>
    </Layout>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/login/*" element={<LoginPage />} />
      <Route path="/signup/*" element={<SignupPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppRoutes />
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}
